import { AxiosResponse } from "axios";
import apiClient from "../../../../shared/infrastructure/api/client";
import { Session, SessionAnalysis, SessionAgreement, SessionNoteHighlight } from "../../domain/Session";
import { SessionWhiteboard } from "../../domain/sessionWhiteboard";
import { AttendanceEntry, AttendanceMark, CourseModuleOption, SessionCourse } from "../../domain/sessionExtras";

type AttendanceEntryDTO = {
  patient_id: string;
  first_name: string;
  last_name: string;
  attended: boolean | null;
};

type CourseModuleDTO = {
  id: string;
  name: string;
  starts_on: string | null;
  ends_on: string | null;
  description?: string | null;
  topics?: string[];
  summary?: string | null;
};

type SessionCourseDTO = {
  course: { id: string; name: string; internal_name: string } | null;
  modules: CourseModuleDTO[];
  module_id: string | null;
  module?: CourseModuleDTO | null;
};

function toCourseModule(module: CourseModuleDTO): CourseModuleOption {
  return {
    id: module.id,
    name: module.name,
    startsOn: module.starts_on,
    endsOn: module.ends_on,
    description: module.description ?? null,
    topics: module.topics ?? [],
    summary: module.summary ?? null,
  };
}

function toAttendanceEntry(dto: AttendanceEntryDTO): AttendanceEntry {
  return { patientId: dto.patient_id, firstName: dto.first_name, lastName: dto.last_name, attended: dto.attended };
}

function toSessionCourse(dto: SessionCourseDTO): SessionCourse {
  return {
    course: dto.course ? { id: dto.course.id, name: dto.course.name, internalName: dto.course.internal_name } : null,
    modules: dto.modules.map(toCourseModule),
    moduleId: dto.module_id,
    module: dto.module ? toCourseModule(dto.module) : null,
  };
}


type WhiteboardDTO = {
  id: string;
  session_id: string;
  created_at: string;
  download_url: string;
};

function toSessionWhiteboard(dto: WhiteboardDTO): SessionWhiteboard {
  return {
    id: dto.id,
    sessionId: dto.session_id,
    createdAt: dto.created_at,
    downloadUrl: dto.download_url,
  };
}
import { LiveInsight } from "../../../../types/live-insights";


interface ListSessionsParams {
  offset?: number;
  limit?: number;
  from_date?: Date | string;
  to_date?: Date | string;
  patient_name?: string;
  status?: string;
  exclude_statuses?: string[];
  session_status?: string;
  payment_status?: string;
}

interface ListPatientSessionsParams {
  offset?: number;
  limit?: number;
  from_date?: Date | string;
  to_date?: Date | string;
  status?: string;
  exclude_statuses?: string[];
}

interface ListSessionsResponse {
  sessions: Session[];
  total: number;
}

export const SessionsAPI = {
  get: (id: string, params?: { log_view?: boolean }): Promise<AxiosResponse<Session>> => apiClient.get(`/sessions/${id}`, { params }),
  create: (data: Partial<Session>): Promise<AxiosResponse<Session>> => apiClient.post("/sessions/", data),
  update: (id: string, data: Partial<Session>): Promise<AxiosResponse<Session>> => apiClient.patch(`/sessions/${id}`, data),
  delete: (id: string): Promise<AxiosResponse<void>> => apiClient.delete(`/sessions/${id}`),

  upload_notes: (id: string, data: FormData): Promise<AxiosResponse<Session>> =>
    apiClient.patch(`/sessions/upload-session-notes/${id}`, data, { headers: { "Content-Type": "multipart/form-data" } }),

  upload_audio: (id: string, data: FormData): Promise<AxiosResponse<Session>> =>
    apiClient.patch(`/sessions/upload-session-audio/${id}`, data, { headers: { "Content-Type": "multipart/form-data" } }),

  get_upload_url: (id: string, content_type: string): Promise<AxiosResponse<{ upload_url: string; storage_path: string }>> =>
    apiClient.get(`/sessions/${id}/upload-url`, { params: { content_type } }),

  trigger_processing: (id: string, data: { storage_path: string; content_type: string; duration_seconds?: number }): Promise<AxiosResponse<{ status: string }>> =>
    apiClient.post(`/sessions/${id}/trigger-processing`, data),

  list_by_therapist: (id: string, params?: ListSessionsParams): Promise<AxiosResponse<ListSessionsResponse>> =>
    apiClient.get(`/sessions/therapist/${id}`, { params }),

  list_by_patient: (id: string, params?: ListPatientSessionsParams): Promise<AxiosResponse<ListSessionsResponse>> =>
    apiClient.get(`/sessions/patient/${id}`, { params }),

  list_by_couple_patient: (id: string, params?: ListPatientSessionsParams): Promise<AxiosResponse<ListSessionsResponse>> =>
    apiClient.get(`/sessions/couple-patient/${id}`, { params }),

  list_by_group_patient: (id: string, params?: ListPatientSessionsParams): Promise<AxiosResponse<ListSessionsResponse>> =>
    apiClient.get(`/sessions/group-patient/${id}`, { params }),

  analyze: (id: string): Promise<AxiosResponse<SessionAnalysis>> => apiClient.patch(`/sessions/analyze/${id}`),
  /** Finish a reviewed session the therapist chose not to have analyzed. */
  completeReview: (id: string): Promise<AxiosResponse<Session>> => apiClient.patch(`/sessions/${id}/review/complete`),
  /** The draft's notes are written: the server settles where it stands. */
  settleNotes: (id: string): Promise<AxiosResponse<Session>> => apiClient.patch(`/sessions/${id}/notes/settle`),
  get_live_insights: (id: string): Promise<AxiosResponse<LiveInsight[]>> => apiClient.get(`/sessions/${id}/live-insights`),
  generate_transcription_notes: (id: string, data: { transcript: string }): Promise<AxiosResponse<string>> => apiClient.patch(`/sessions/generate-transcription-notes/${id}`, data),
  reformat: (id: string, data: { template_id: string }): Promise<AxiosResponse<Session>> => apiClient.patch(`/sessions/reformat/${id}`, data),

  // Agreements
  getAgreements: (sessionId: string): Promise<AxiosResponse<SessionAgreement[]>> =>
    apiClient.get(`/sessions/${sessionId}/agreements`),
  createAgreement: (sessionId: string, text: string): Promise<AxiosResponse<SessionAgreement>> =>
    apiClient.post(`/sessions/${sessionId}/agreements`, { text }),
  updateAgreement: (agreementId: string, data: { text?: string; order?: number }): Promise<AxiosResponse<SessionAgreement>> =>
    apiClient.patch(`/sessions/agreements/${agreementId}`, data),
  deleteAgreement: (agreementId: string): Promise<AxiosResponse<void>> =>
    apiClient.delete(`/sessions/agreements/${agreementId}`),
  publishAgreements: (sessionId: string): Promise<AxiosResponse<SessionAgreement[]>> =>
    apiClient.post(`/sessions/${sessionId}/agreements/publish`),

  // Highlights
  /** Highlights across all of a patient's sessions (served under /patients). */
  getPatientHighlights: (patientId: string): Promise<AxiosResponse<SessionNoteHighlight[]>> =>
    apiClient.get(`/patients/${patientId}/highlights`),
  getHighlights: (sessionId: string): Promise<AxiosResponse<SessionNoteHighlight[]>> =>
    apiClient.get(`/sessions/${sessionId}/highlights`),
  createHighlight: (sessionId: string, data: { text: string; patient_id: string }): Promise<AxiosResponse<SessionNoteHighlight>> =>
    apiClient.post(`/sessions/${sessionId}/highlights`, data),
  deleteHighlight: (highlightId: string): Promise<AxiosResponse<void>> =>
    apiClient.delete(`/highlights/${highlightId}`),

  // Whiteboards. Saved only when the therapist asks for it; the download URL
  // that comes back is short-lived and is re-issued on every list. The wire
  // shape is mapped here so the module's own type stays free of it.
  getWhiteboards: async (sessionId: string): Promise<SessionWhiteboard[]> => {
    const response = await apiClient.get<WhiteboardDTO[]>(`/sessions/${sessionId}/whiteboards`);
    return response.data.map(toSessionWhiteboard);
  },
  saveWhiteboard: async (sessionId: string, imageBase64: string): Promise<SessionWhiteboard> => {
    const response = await apiClient.post<WhiteboardDTO>(`/sessions/${sessionId}/whiteboards`, {
      image_base64: imageBase64,
    });
    return toSessionWhiteboard(response.data);
  },
  deleteWhiteboard: (sessionId: string, whiteboardId: string): Promise<AxiosResponse<void>> =>
    apiClient.delete(`/sessions/${sessionId}/whiteboards/${whiteboardId}`),

  getAttendance: async (sessionId: string): Promise<AttendanceEntry[]> => {
    const response = await apiClient.get<AttendanceEntryDTO[]>(`/sessions/${sessionId}/attendance`);
    return response.data.map(toAttendanceEntry);
  },
  recordAttendance: async (sessionId: string, marks: AttendanceMark[]): Promise<AttendanceEntry[]> => {
    const response = await apiClient.put<AttendanceEntryDTO[]>(`/sessions/${sessionId}/attendance`, {
      records: marks.map((mark) => ({ patient_id: mark.patientId, attended: mark.attended })),
    });
    return response.data.map(toAttendanceEntry);
  },

  getCourse: async (sessionId: string): Promise<SessionCourse> => {
    const response = await apiClient.get<SessionCourseDTO>(`/sessions/${sessionId}/course`);
    return toSessionCourse(response.data);
  },
  assignCourseModule: async (sessionId: string, moduleId: string | null): Promise<SessionCourse> => {
    const response = await apiClient.put<SessionCourseDTO>(`/sessions/${sessionId}/course-module`, {
      module_id: moduleId,
    });
    return toSessionCourse(response.data);
  },
};
